import { createClient } from '@supabase/supabase-js';

/**
 * Create a Supabase client with the auth settings shared by every Astra XR app.
 * Platform-specific concerns (session storage, URL session detection) are injected.
 *
 * @param {object} options
 * @param {string} options.url
 * @param {string} options.anonKey
 * @param {object} [options.storage] - Supabase auth storage adapter. Defaults to the platform's localStorage.
 * @param {string} [options.storageKey='supabase.auth.token']
 * @param {boolean} [options.detectSessionInUrl=false]
 * @param {string} options.clientInfo - Sent as the X-Client-Info header.
 */
export function createSupabaseClient({
  url,
  anonKey,
  storage,
  storageKey = 'supabase.auth.token',
  detectSessionInUrl = false,
  clientInfo,
}) {
  if (!url || !anonKey) {
    throw new Error('Missing Supabase configuration: both url and anonKey are required.');
  }

  const client = createClient(url, anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl,
      storageKey,
      ...(storage ? { storage } : {}),
      flowType: 'pkce',
    },
    global: {
      headers: {
        'X-Client-Info': clientInfo,
      },
    },
    db: {
      schema: 'public',
    },
  });

  // Recover from JWTIssuedAtFuture errors caused by device clock skew.
  const originalRequest = client.rest.fetch;
  client.rest.fetch = async (...args) => {
    try {
      return await originalRequest.apply(client.rest, args);
    } catch (error) {
      if (error?.message?.includes('JWTIssuedAtFuture')) {
        console.warn('[Supabase] Clock skew detected, refreshing session...');
        const { data, error: refreshError } = await client.auth.refreshSession();
        if (!refreshError && data?.session) {
          return await originalRequest.apply(client.rest, args);
        }
      }
      throw error;
    }
  };

  return client;
}
